import React, { useState, useEffect, useCallback } from 'react';
import { Grid, Card, CardContent, Typography, Box, CircularProgress, Chip } from '@mui/material';
import {
	LineChart,
	Line,
	AreaChart,
	Area,
	BarChart,
	Bar,
	PieChart,
	Pie,
	Cell,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	ResponsiveContainer,
} from 'recharts';
import {
	TrendingUp as TrendingUpIcon,
	People as PeopleIcon,
	Visibility as VisibilityIcon,
	AccessTime as TimeIcon,
	Computer as ComputerIcon,
	Smartphone as SmartphoneIcon,
	Description as DescriptionIcon,
	FileDownload as FileDownloadIcon,
	Article as ArticleIcon,
	Quiz as QuizIcon,
	AssignmentTurnedIn as AssignmentTurnedInIcon,
	NoteAdd as NoteAddIcon,
	QuestionAnswer as QuestionAnswerIcon,
	Assessment as AssessmentIcon,
	Insights as InsightsIcon,
} from '@mui/icons-material';
import { useErrorSetter } from '../hooks/useUiMessages';
import { fetchAnalyticsSummary, fetchDailyTrends, fetchRealtimeAnalytics, fetchUsersCount, fetchProductFunnels, fetchLifetimeAnalytics, transformDailyTrends, transformConversionFunnel, fetchTransactionsCount } from './analyticsApi';
import styles from './Analytics.module.css';

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088fe', '#00c49f'];

const AnalyticsOverview = ({ dateRange }) => {
	const setError = useErrorSetter();
	const [loading, setLoading] = useState(true);
	const [overviewData, setOverviewData] = useState({
		summary: {
			totalSessions: 0,
			totalMessages: 0,
			totalUsers: 0,
			totalTransactions: 0,
			conversionRate: 0,
			averageSessionDuration: 0,
			topCountries: [],
		},
		dailyTrends: [],
		userTypeDistribution: [],
		deviceBreakdown: [],
		hourlyActivity: [],
		conversionFunnel: [],
	});

	// Daily Trends: toggle series visibility to reduce clutter
	const [visibleSeries, setVisibleSeries] = useState({
		sessions: true,
		users: true,
		messages: true,
		jobsFetched: true,
		resumeCreated: false,
		resumeOptimized: false,
		resumeDownloaded: false,
		coverCreated: false,
		coverDownloaded: false,
		interviewGenerated: false,
		loginSuccess: false,
		loginFailed: false,
		signupCompleted: false,
		examGenerated: true,
		examGraded: true,
		studyPlanGenerated: false,
		tutorQuestions: false,
		benchmarkingUsed: false,
		insightGenerated: false,
		careerRoadmapGenerated: false,
	});

	const SERIES_CONFIG = [
		{ key: 'sessions', name: 'Sessions', color: COLORS[0] },
		{ key: 'users', name: 'Users', color: COLORS[1] },
		{ key: 'messages', name: 'Messages', color: COLORS[2] },
		{ key: 'loginSuccess', name: 'Logins', color: '#3cba54' },
		{ key: 'loginFailed', name: 'Login Failed', color: '#db3236' },
		{ key: 'signupCompleted', name: 'Signups', color: '#f4c20d' },
		{ key: 'resumeCreated', name: 'Resumes Created', color: '#1f77b4' },
		{ key: 'resumeOptimized', name: 'Resumes Optimized', color: '#17becf' },
		{ key: 'resumeDownloaded', name: 'Resumes Downloaded', color: '#2ca02c' },
		{ key: 'coverCreated', name: 'Cover Letters Created', color: '#ff7f0e' },
		{ key: 'coverDownloaded', name: 'Cover Letters Downloaded', color: '#9467bd' },
		{ key: 'interviewGenerated', name: 'Interviews Generated', color: '#9467bd' },
		{ key: 'jobsFetched', name: 'Jobs Fetched', color: '#17becf' },
		{ key: 'careerRoadmapGenerated', name: 'Career Roadmaps', color: '#db688e' },
		{ key: 'examGenerated', name: 'Exams Generated', color: '#8884d8' },
		{ key: 'examGraded', name: 'Exams Graded', color: '#82ca9d' },
		{ key: 'studyPlanGenerated', name: 'Study Plans', color: '#ffc658' },
		{ key: 'tutorQuestions', name: 'Tutor Qs', color: '#ff7300' },
		{ key: 'benchmarkingUsed', name: 'Benchmarking', color: '#0088fe' },
		{ key: 'insightGenerated', name: 'Insights', color: '#00c49f' },
	];

	const toggleSeries = useCallback((key) => {
		setVisibleSeries((prev) => ({ ...prev, [key]: !prev[key] }));
	}, []);

		const fetchOverviewData = useCallback(async () => {
		try {
			setLoading(true);
			const token = sessionStorage.getItem('afd8TvhsdjwiuuvsgjhsAfgsUhjs');
			if (!token) {
				// Auth required: show empty safe state, do not use mock numbers
				setOverviewData((prev) => ({ ...prev, summary: {}, dailyTrends: [], conversionFunnel: [] }));
				return;
			}
			const startISO = dateRange?.start?.toISOString?.() || null;
			const endISO = dateRange?.end?.toISOString?.() || null;
					// Fetch summary, daily trends, realtime, users count and product funnels in parallel for efficiency
					const [summaryPayload, dailyPayload, realtimePayload, usersCountRes, transactionsCountRes, funnels, lifetimeRes] = await Promise.all([
						fetchAnalyticsSummary(startISO, endISO, 'all'),
						fetchDailyTrends(startISO, endISO),
						fetchRealtimeAnalytics(),
						fetchUsersCount(),
						fetchTransactionsCount(),
						fetchProductFunnels(startISO, endISO),
						fetchLifetimeAnalytics()
					]);

						// Compute previous period deltas (same length previous window)
					let deltas = { sessions: 0, users: 0, messages: 0, conversionRate: 0 };
					if (dateRange?.start && dateRange?.end) {
						const ms = dateRange.end.getTime() - dateRange.start.getTime();
						const prevEnd = new Date(dateRange.start.getTime() - 1);
						const prevStart = new Date(prevEnd.getTime() - ms);
						const prevSummary = await fetchAnalyticsSummary(prevStart.toISOString(), prevEnd.toISOString(), 'all');
						const s1 = summaryPayload?.summary || {}; const s0 = prevSummary?.summary || {};
						const val = (x) => (typeof x === 'number' && !Number.isNaN(x)) ? x : 0;
						const deltaPct = (now, prev) => prev ? ((now - prev) / prev) * 100 : (now ? 100 : 0);
						deltas = {
							sessions: deltaPct(val(s1.totalSessions), val(s0.totalSessions)),
							users: deltaPct(val((s1.authUsers||0)+(s1.unauthUsers||0)), val((s0.authUsers||0)+(s0.unauthUsers||0))),
								messages: deltaPct(val(s1.messages), val(s0.messages)),
							conversionRate: deltaPct(val(s1.conversionRate), val(s0.conversionRate))
						};
					}

			const summary = summaryPayload?.summary || {};
			const dailyTrends = transformDailyTrends(dailyPayload);
			const conversionFunnel = transformConversionFunnel(summaryPayload?.conversionFunnelMetrics || summaryPayload?.conversionMetrics || {});

			// Normalize device breakdown from realtime payload
			const deviceBreakdown = (() => {
				const raw = realtimePayload?.deviceBreakdown || [];
				if (!Array.isArray(raw)) return [];
				return raw.map((d) => {
					// accept { deviceType/count } or { _id/count } or { device/count }
					const device = d.device || d.deviceType || d._id || 'unknown';
					const count = Number(d.count || d.total || d.value || 0);
					return { device, count };
				}).filter(Boolean);
			})();

			// Normalize hourly activity: sum across user types into { hour, activity }
			const hourlyActivity = (() => {
				const raw = realtimePayload?.hourlyBreakdown || [];
				if (!Array.isArray(raw)) return [];
				// raw may be [{ _id: { hour, userType }, count }]
				const map = new Map();
				raw.forEach((r) => {
					const hour = (r._id && (r._id.hour ?? r.hour)) ?? r.hour ?? 0;
					const key = String(hour).padStart(2, '0');
					const prev = map.get(key) || 0;
					map.set(key, prev + Number(r.count || r.total || 0));
				});
				// sort by hour asc
				return Array.from(map.entries())
					.sort(([a], [b]) => a.localeCompare(b))
					.map(([hour, activity]) => ({ hour, activity }));
			})();

	    setOverviewData({
				summary: {
					totalSessions: summary.totalSessions || 0,
					totalMessages: summary.messages || 0,
	    totalUsers: usersCountRes?.count || 0,
	    totalTransactions: transactionsCountRes?.count || 0,
		    activeUsers: (summary.authUsers || 0) + (summary.unauthUsers || 0),
					conversionRate: summary.conversionRate || 0,
					averageSessionDuration: summary.avgSessionDuration || 0,
					topCountries: (summaryPayload.geographicData || []).slice(0, 3).map((c) => c._id || c.country).filter(Boolean),
				},
				dailyTrends,
						userTypeDistribution: (() => {
							const a = summary.authUsers || 0;
							const g = summary.unauthUsers || 0;
							const total = a + g;
							const pct = (v) => (total ? (v / total) * 100 : 0);
							return [
								{ type: 'Authenticated', count: a, percentage: pct(a) },
								{ type: 'Guest', count: g, percentage: pct(g) },
							];
						})(),
				deviceBreakdown,
				hourlyActivity,
				conversionFunnel,
				serviceTotals: {
					resumeCreated: funnels?.resume?.created || 0,
					resumeDownloaded: funnels?.resume?.downloaded || 0,
					coverCreated: funnels?.coverLetter?.created || 0,
					interviewGenerated: funnels?.interview?.generated || 0,
					jobsFetchedSession: funnels?.jobs?.completed || 0,
					examGenerated: funnels?.exam?.generated || 0,
					examGraded: funnels?.exam?.graded || 0,
					studyPlanGenerated: funnels?.exam?.studyPlan || 0,
					tutorQuestions: funnels?.exam?.tutorQuestions || 0,
					benchmarkingUsed: funnels?.exam?.benchmarking || 0,
					insightGenerated: funnels?.exam?.insight || 0,
					careerRoadmapGenerated: funnels?.career?.roadmapGenerated || 0,
					lifetime: {
						messages: lifetimeRes?.lifetime?.messages || 0,
						resumesCreated: lifetimeRes?.lifetime?.resumesCreated || 0,
						resumesDownloaded: lifetimeRes?.lifetime?.resumesDownloaded || 0,
						resumesOptimized: lifetimeRes?.lifetime?.resumesOptimized || 0,
						coverLettersCreated: lifetimeRes?.lifetime?.coverLettersCreated || 0,
						coverLettersDownloaded: lifetimeRes?.lifetime?.coverLettersDownloaded || 0,
						interviewsGenerated: lifetimeRes?.lifetime?.interviewsGenerated || 0,
						jobFetchedLifetime: lifetimeRes?.lifetime?.jobFetched?.lifetime || 0,
						jobFetchedSession: lifetimeRes?.lifetime?.jobFetched?.session || 0,
						examGenerated: lifetimeRes?.lifetime?.exam?.generated || 0,
						examCustom: lifetimeRes?.lifetime?.exam?.custom || 0,
						examPrebuilt: lifetimeRes?.lifetime?.exam?.prebuilt || 0,
						examGraded: lifetimeRes?.lifetime?.exam?.graded || 0,
						studyPlan: lifetimeRes?.lifetime?.exam?.studyPlan || 0,
						tutorQuestions: lifetimeRes?.lifetime?.exam?.tutorQuestions || 0,
						benchmarking: lifetimeRes?.lifetime?.exam?.benchmarking || 0,
						insight: lifetimeRes?.lifetime?.exam?.insight || 0,
						careerRoadmap: lifetimeRes?.lifetime?.career?.roadmapGenerated || 0
					}
				},
			});
					// Attach deltas to summary for quick rendering (non-breaking)
					setOverviewData(prev => ({ ...prev, summary: { ...prev.summary, deltas } }));
		} catch (err) {
			console.error('Overview fetch error:', err);
			setError('Failed to load analytics overview');
		} finally {
			setLoading(false);
		}
	}, [dateRange, setError]);

	useEffect(() => {
		fetchOverviewData();
	}, [fetchOverviewData]);

	// Helper functions for safe data rendering
	const formatNumber = (num) => {
		if (num == null || Number.isNaN(num)) return '0';
		if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
		if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
		return num.toString();
	};

	const formatDuration = (seconds) => {
		if (seconds == null || Number.isNaN(seconds)) return '0:00';
		const minutes = Math.floor(seconds / 60);
		const remainingSeconds = Math.floor(seconds % 60);
		return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
	};

	const formatPercentage = (value) => {
		if (value == null || Number.isNaN(value)) return '0.0%';
		return `${parseFloat(value).toFixed(1)}%`;
	};

	// Safe data access with fallbacks
	const safeData = {
		summary: overviewData?.summary || {},
		dailyTrends: overviewData?.dailyTrends || [],
		userTypeDistribution: overviewData?.userTypeDistribution || [],
		deviceBreakdown: overviewData?.deviceBreakdown || [],
		hourlyActivity: overviewData?.hourlyActivity || [],
	conversionFunnel: overviewData?.conversionFunnel || [],
	serviceTotals: overviewData?.serviceTotals || {},
	};

	if (loading) {
		return (
			<Box className={styles.loadingContainer}>
				<CircularProgress />
				<Typography variant="body1" sx={{ mt: 2 }}>
					Loading overview data...
				</Typography>
			</Box>
		);
	}

	return (
		<div className={styles.overviewContainer}>
			{/* Summary Cards */}
			<Grid container spacing={3} className={styles.metricsGrid}>
				<Grid item xs={12} sm={6} md={2}>
					<Card className={styles.metricCard}>
						<CardContent>
							<Box display="flex" alignItems="center" mb={1}>
								<VisibilityIcon color="primary" sx={{ mr: 1 }} />
								<Typography variant="h6" color="textSecondary">
									Total Sessions
								</Typography>
							</Box>
							<Typography variant="h4" className={styles.metricValue}>
								{formatNumber(safeData.summary.totalSessions)}
							</Typography>
							{safeData.summary.deltas && (
								<Chip size="small" label={`${safeData.summary.deltas.sessions.toFixed(1)}%`} color={safeData.summary.deltas.sessions >= 0 ? 'success' : 'error'} sx={{ mt: 1 }} />
							)}
						</CardContent>
					</Card>
				</Grid>

				<Grid item xs={12} sm={6} md={2}>
					<Card className={styles.metricCard}>
						<CardContent>
							<Box display="flex" alignItems="center" mb={1}>
								<PeopleIcon color="success" sx={{ mr: 1 }} />
								<Typography variant="h6" color="textSecondary">
									Total Users
								</Typography>
							</Box>
							<Typography variant="h4" className={styles.metricValue}>
								{safeData.summary.totalUsers}
							</Typography>
							<Chip size="small" label={`lifetime`} color={'default'} sx={{ mt: 1 }} />
						</CardContent>
					</Card>
				</Grid>

				{/* Total Transactions */}
				<Grid item xs={12} sm={6} md={2}>
					<Card className={styles.metricCard}>
						<CardContent>
							<Box display="flex" alignItems="center" mb={1}>
								<PeopleIcon color="secondary" sx={{ mr: 1 }} />
								<Typography variant="h6" color="textSecondary">
									Total Transactions
								</Typography>
							</Box>
							<Typography variant="h4" className={styles.metricValue}>
								{formatNumber(safeData.summary.totalTransactions)}
							</Typography>
							<Chip size="small" label={`lifetime`} color={'default'} sx={{ mt: 1 }} />
						</CardContent>
					</Card>
				</Grid>

				<Grid item xs={12} sm={6} md={2}>
					<Card className={styles.metricCard}>
						<CardContent>
							<Box display="flex" alignItems="center" mb={1}>
								<ComputerIcon color="info" sx={{ mr: 1 }} />
								<Typography variant="h6" color="textSecondary">
									Messages
								</Typography>
							</Box>
							<Typography variant="h4" className={styles.metricValue}>
								{formatNumber(safeData.summary.totalMessages)}
							</Typography>
							{overviewData?.serviceTotals?.lifetime && (
								<Chip size="small" label={`LT ${formatNumber(overviewData.serviceTotals.lifetime.messages)}`} sx={{ mt: 1 }} />
							)}
							{/* Lifetime auth chips */}
							{overviewData?.serviceTotals?.lifetime?.auth && (
								<Box display="flex" gap={1} mt={1} flexWrap="wrap">
									<Chip size="small" label={`LT Logins ${formatNumber(overviewData.serviceTotals.lifetime.auth.logins)}`} />
									<Chip size="small" label={`LT Signups ${formatNumber(overviewData.serviceTotals.lifetime.auth.signups)}`} />
									<Chip size="small" label={`LT Login Failures ${formatNumber(overviewData.serviceTotals.lifetime.auth.loginFailures)}`} />
								</Box>
							)}
											{safeData.summary.deltas && (
												<Chip size="small" label={`${safeData.summary.deltas.messages.toFixed(1)}%`} color={safeData.summary.deltas.messages >= 0 ? 'success' : 'error'} sx={{ mt: 1 }} />
											)}
						</CardContent>
					</Card>
				</Grid>

					{/* Services Totals (same styling as other summary cards) */}
					<Grid item xs={12} sm={6} md={2}>
						<Card className={styles.metricCard}>
							<CardContent>
								<Box display="flex" alignItems="center" mb={1}>
									<DescriptionIcon color="primary" sx={{ mr: 1 }} />
									<Typography variant="h6" color="textSecondary">Resumes Created</Typography>
								</Box>
								<Typography variant="h4" className={styles.metricValue}>{formatNumber(overviewData?.serviceTotals?.resumeCreated || 0)}</Typography>
								{overviewData?.serviceTotals?.lifetime && (
									<Chip size="small" label={`LT ${formatNumber(overviewData.serviceTotals.lifetime.resumesCreated)}`} sx={{ mt: 1 }} />
								)}
								{overviewData?.serviceTotals?.lifetime && overviewData?.serviceTotals?.lifetime?.resumesOptimized != null && (
									<Chip size="small" label={`LT Optimized ${formatNumber(overviewData.serviceTotals.lifetime.resumesOptimized)}`} sx={{ mt: 1, ml: 1 }} />
								)}
							</CardContent>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6} md={2}>
						<Card className={styles.metricCard}>
							<CardContent>
								<Box display="flex" alignItems="center" mb={1}>
									<FileDownloadIcon color="success" sx={{ mr: 1 }} />
									<Typography variant="h6" color="textSecondary">Resumes Downloaded</Typography>
								</Box>
								<Typography variant="h4" className={styles.metricValue}>{formatNumber(overviewData?.serviceTotals?.resumeDownloaded || 0)}</Typography>
								{overviewData?.serviceTotals?.lifetime && (
									<Chip size="small" label={`LT ${formatNumber(overviewData.serviceTotals.lifetime.resumesDownloaded)}`} sx={{ mt: 1, position: 'relative', bottom: '2px' }} />
								)}
							</CardContent>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6} md={2}>
						<Card className={styles.metricCard}>
							<CardContent>
								<Box display="flex" alignItems="center" mb={1}>
									<ArticleIcon color="warning" sx={{ mr: 1 }} />
									<Typography variant="h6" color="textSecondary">Cover Letters Generated</Typography>
								</Box>
								<Typography variant="h4" className={styles.metricValue}>{formatNumber(overviewData?.serviceTotals?.coverCreated || 0)}</Typography>
								{overviewData?.serviceTotals?.lifetime && (
									<Chip size="small" label={`LT ${formatNumber(overviewData.serviceTotals.lifetime.coverLettersCreated)}`} sx={{ mt: 1 }} />
								)}
								{overviewData?.serviceTotals?.lifetime && overviewData?.serviceTotals?.lifetime?.coverLettersDownloaded != null && (
									<Chip size="small" label={`LT Downloaded ${formatNumber(overviewData.serviceTotals.lifetime.coverLettersDownloaded)}`} sx={{ mt: 1, ml: 1 }} />
								)}
							</CardContent>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6} md={2}>
						<Card className={styles.metricCard}>
							<CardContent>
								<Box display="flex" alignItems="center" mb={1}>
									<QuizIcon color="info" sx={{ mr: 1 }} />
									<Typography variant="h6" color="textSecondary">Interviews Generated</Typography>
								</Box>
								<Typography variant="h4" className={styles.metricValue}>{formatNumber(overviewData?.serviceTotals?.interviewGenerated || 0)}</Typography>
								{overviewData?.serviceTotals?.lifetime && (
									<Chip size="small" label={`LT ${formatNumber(overviewData.serviceTotals.lifetime.interviewsGenerated)}`} sx={{ mt: 1 }} />
								)}
							</CardContent>
						</Card>
					</Grid>

					{/* Job Fetched: session and lifetime */}
					<Grid item xs={12} sm={6} md={2}>
						<Card className={styles.metricCard}>
							<CardContent>
								<Box display="flex" alignItems="center" mb={1}>
									<TrendingUpIcon color="secondary" sx={{ mr: 1 }} />
									<Typography variant="h6" color="textSecondary">Jobs Fetched</Typography>
								</Box>
								<Typography variant="h4" className={styles.metricValue}>{formatNumber(overviewData?.serviceTotals?.jobsFetchedSession || 0)}</Typography>
								{overviewData?.serviceTotals?.lifetime && (
									<Chip size="small" label={`LT ${formatNumber(overviewData.serviceTotals.lifetime.jobFetchedLifetime)}`} sx={{ mt: 1 }} />
								)}
							</CardContent>
						</Card>
					</Grid>

					{/* Career Roadmaps */}
					<Grid item xs={12} sm={6} md={2}>
						<Card className={styles.metricCard}>
							<CardContent>
								<Box display="flex" alignItems="center" mb={1}>
									<InsightsIcon color="primary" sx={{ mr: 1 }} />
									<Typography variant="h6" color="textSecondary">Career Roadmaps</Typography>
								</Box>
								<Typography variant="h4" className={styles.metricValue}>{formatNumber(overviewData?.serviceTotals?.careerRoadmapGenerated || 0)}</Typography>
								{overviewData?.serviceTotals?.lifetime && (
									<Chip size="small" label={`LT ${formatNumber(overviewData.serviceTotals.lifetime.careerRoadmap || 0)}`} sx={{ mt: 1 }} />
								)}
							</CardContent>
						</Card>
					</Grid>

					{/* Exam Simulation Tiles */}
					<Grid item xs={12} sm={6} md={2}>
						<Card className={styles.metricCard}>
							<CardContent>
								<Box display="flex" alignItems="center" mb={1}>
									<DescriptionIcon color="primary" sx={{ mr: 1 }} />
									<Typography variant="h6" color="textSecondary">Exams Generated</Typography>
								</Box>
								<Typography variant="h4" className={styles.metricValue}>{formatNumber(overviewData?.serviceTotals?.examGenerated || 0)}</Typography>
								{overviewData?.serviceTotals?.lifetime && (
									<Box display="flex" flexDirection="column" gap={0.5} mt={1}>
										<Chip size="small" label={`LT Custom: ${formatNumber(overviewData.serviceTotals.lifetime.examCustom)}`} />
										<Chip size="small" label={`LT Prebuilt: ${formatNumber(overviewData.serviceTotals.lifetime.examPrebuilt)}`} />
									</Box>
								)}
							</CardContent>
						</Card>
					</Grid>

					<Grid item xs={12} sm={6} md={2}>
						<Card className={styles.metricCard}>
							<CardContent>
								<Box display="flex" alignItems="center" mb={1}>
									<AssignmentTurnedInIcon color="success" sx={{ mr: 1 }} />
									<Typography variant="h6" color="textSecondary">Exams Graded</Typography>
								</Box>
								<Typography variant="h4" className={styles.metricValue}>{formatNumber(overviewData?.serviceTotals?.examGraded || 0)}</Typography>
								{overviewData?.serviceTotals?.lifetime && (
									<Chip size="small" label={`LT ${formatNumber(overviewData.serviceTotals.lifetime.examGraded)}`} sx={{ mt: 1 }} />
								)}
							</CardContent>
						</Card>
					</Grid>

					<Grid item xs={12} sm={6} md={2}>
						<Card className={styles.metricCard}>
							<CardContent>
								<Box display="flex" alignItems="center" mb={1}>
									<NoteAddIcon color="warning" sx={{ mr: 1 }} />
									<Typography variant="h6" color="textSecondary">Study Plans</Typography>
								</Box>
								<Typography variant="h4" className={styles.metricValue}>{formatNumber(overviewData?.serviceTotals?.studyPlanGenerated || 0)}</Typography>
								{overviewData?.serviceTotals?.lifetime && (
									<Chip size="small" label={`LT ${formatNumber(overviewData.serviceTotals.lifetime.studyPlan)}`} sx={{ mt: 1 }} />
								)}
							</CardContent>
						</Card>
					</Grid>

					<Grid item xs={12} sm={6} md={2}>
						<Card className={styles.metricCard}>
							<CardContent>
								<Box display="flex" alignItems="center" mb={1}>
									<QuestionAnswerIcon color="info" sx={{ mr: 1 }} />
									<Typography variant="h6" color="textSecondary">Tutor Questions</Typography>
								</Box>
								<Typography variant="h4" className={styles.metricValue}>{formatNumber(overviewData?.serviceTotals?.tutorQuestions || 0)}</Typography>
								{overviewData?.serviceTotals?.lifetime && (
									<Chip size="small" label={`LT ${formatNumber(overviewData.serviceTotals.lifetime.tutorQuestions)}`} sx={{ mt: 1 }} />
								)}
							</CardContent>
						</Card>
					</Grid>

					<Grid item xs={12} sm={6} md={2}>
						<Card className={styles.metricCard}>
							<CardContent>
								<Box display="flex" alignItems="center" mb={1}>
									<AssessmentIcon color="secondary" sx={{ mr: 1 }} />
									<Typography variant="h6" color="textSecondary">Benchmarking</Typography>
								</Box>
								<Typography variant="h4" className={styles.metricValue}>{formatNumber(overviewData?.serviceTotals?.benchmarkingUsed || 0)}</Typography>
								{overviewData?.serviceTotals?.lifetime && (
									<Chip size="small" label={`LT ${formatNumber(overviewData.serviceTotals.lifetime.benchmarking)}`} sx={{ mt: 1 }} />
								)}
							</CardContent>
						</Card>
					</Grid>

					<Grid item xs={12} sm={6} md={2}>
						<Card className={styles.metricCard}>
							<CardContent>
								<Box display="flex" alignItems="center" mb={1}>
									<InsightsIcon style={{ color: '#00c49f' }} sx={{ mr: 1 }} />
									<Typography variant="h6" color="textSecondary">Exam Insights</Typography>
								</Box>
								<Typography variant="h4" className={styles.metricValue}>{formatNumber(overviewData?.serviceTotals?.insightGenerated || 0)}</Typography>
								{overviewData?.serviceTotals?.lifetime && (
									<Chip size="small" label={`LT ${formatNumber(overviewData.serviceTotals.lifetime.insight)}`} sx={{ mt: 1 }} />
								)}
							</CardContent>
						</Card>
					</Grid>

				<Grid item xs={12} sm={6} md={2}>
					<Card className={styles.metricCard}>
						<CardContent>
							<Box display="flex" alignItems="center" mb={1}>
								<TrendingUpIcon color="warning" sx={{ mr: 1 }} />
								<Typography variant="h6" color="textSecondary">
									Conversion Rate
								</Typography>
							</Box>
											<Typography variant="h4" className={styles.metricValue}>
												{formatPercentage(safeData.summary.conversionRate)}
											</Typography>
											{safeData.summary.deltas && (
												<Chip size="small" label={`${safeData.summary.deltas.conversionRate.toFixed(1)}%`} color={safeData.summary.deltas.conversionRate >= 0 ? 'success' : 'error'} sx={{ mt: 1 }} />
											)}
						</CardContent>
					</Card>
				</Grid>

				<Grid item xs={12} sm={6} md={2}>
					<Card className={styles.metricCard}>
						<CardContent>
							<Box display="flex" alignItems="center" mb={1}>
								<TimeIcon color="secondary" sx={{ mr: 1 }} />
								<Typography variant="h6" color="textSecondary">
									Avg Session
								</Typography>
							</Box>
							<Typography variant="h4" className={styles.metricValue}>
								{formatDuration(safeData.summary.averageSessionDuration)}
							</Typography>
						</CardContent>
					</Card>
				</Grid>

				<Grid item xs={12} sm={6} md={2}>
					<Card className={styles.metricCard}>
						<CardContent>
							<Box display="flex" alignItems="center" mb={1}>
								<SmartphoneIcon color="primary" sx={{ mr: 1 }} />
								<Typography variant="h6" color="textSecondary">
									Top Countries
								</Typography>
							</Box>
							<Box>
								{(safeData.summary.topCountries || []).slice(0, 3).map((country, index) => (
									<Chip
										key={index}
										label={country}
										size="small"
										sx={{ mr: 0.5, mb: 0.5 }}
										color="primary"
										variant="outlined"
									/>
								))}
							</Box>
						</CardContent>
					</Card>
				</Grid>
			</Grid>

			{/* Charts Grid */}
			<Grid container spacing={3} className={styles.chartsGrid}>
				{/* Daily Trends */}
				<Grid item xs={12} lg={8}>
					<Card className={styles.chartCard}>
						<CardContent>
							<Typography variant="h6" className={styles.chartTitle}>
								Daily Activity Trends
							</Typography>
							<ResponsiveContainer width="100%" height={300}>
								<LineChart data={safeData.dailyTrends}>
									<CartesianGrid strokeDasharray="3 3" />
									<XAxis dataKey="date" />
									<YAxis />
									<Tooltip />
									<Legend content={() => (
										<Box display="flex" flexWrap="wrap" gap={1}>
											{SERIES_CONFIG.filter(item => !['examGenerated', 'examGraded', 'studyPlanGenerated', 'tutorQuestions', 'benchmarkingUsed', 'insightGenerated'].includes(item.key)).map((item) => {
												const key = item.key;
												const active = !!visibleSeries[key];
												return (
													<Chip
														key={key}
														label={item.name}
														onClick={() => toggleSeries(key)}
														variant={active ? 'filled' : 'outlined'}
														sx={{ cursor: 'pointer', bgcolor: active ? item.color : 'transparent', color: active ? '#fff' : 'inherit', borderColor: item.color }}
													/>
												);
											})}
										</Box>
									)} />
									<Line type="monotone" dataKey="sessions" stroke={COLORS[0]} strokeWidth={2} name="Sessions" hide={!visibleSeries.sessions} />
									<Line type="monotone" dataKey="users" stroke={COLORS[1]} strokeWidth={2} name="Users" hide={!visibleSeries.users} />
									<Line type="monotone" dataKey="messages" stroke={COLORS[2]} strokeWidth={2} name="Messages" hide={!visibleSeries.messages} />
									<Line type="monotone" dataKey="loginSuccess" stroke="#3cba54" strokeWidth={2} name="Logins" hide={!visibleSeries.loginSuccess} />
									<Line type="monotone" dataKey="loginFailed" stroke="#db3236" strokeWidth={2} name="Login Failed" hide={!visibleSeries.loginFailed} />
									<Line type="monotone" dataKey="signupCompleted" stroke="#f4c20d" strokeWidth={2} name="Signups" hide={!visibleSeries.signupCompleted} />
									<Line type="monotone" dataKey="resumeCreated" stroke="#1f77b4" strokeWidth={2} name="Resumes Created" hide={!visibleSeries.resumeCreated} />
									<Line type="monotone" dataKey="resumeOptimized" stroke="#17becf" strokeWidth={2} name="Resumes Optimized" hide={!visibleSeries.resumeOptimized} />
									<Line type="monotone" dataKey="resumeDownloaded" stroke="#2ca02c" strokeWidth={2} name="Resumes Downloaded" hide={!visibleSeries.resumeDownloaded} />
									<Line type="monotone" dataKey="coverCreated" stroke="#ff7f0e" strokeWidth={2} name="Cover Letters Created" hide={!visibleSeries.coverCreated} />
									<Line type="monotone" dataKey="coverDownloaded" stroke="#9467bd" strokeWidth={2} name="Cover Letters Downloaded" hide={!visibleSeries.coverDownloaded} />
									<Line type="monotone" dataKey="interviewGenerated" stroke="#9467bd" strokeWidth={2} name="Interviews Generated" hide={!visibleSeries.interviewGenerated} />
									<Line type="monotone" dataKey="jobsFetched" stroke="#17becf" strokeWidth={2} name="Jobs Fetched" hide={!visibleSeries.jobsFetched} />
									<Line type="monotone" dataKey="careerRoadmapGenerated" stroke="#db688e" strokeWidth={2} name="Career Roadmaps" hide={!visibleSeries.careerRoadmapGenerated} />
								</LineChart>
							</ResponsiveContainer>
						</CardContent>
					</Card>
				</Grid>

				{/* Exam Simulation Trends */}
				<Grid item xs={12} lg={8}>
					<Card className={styles.chartCard}>
						<CardContent>
							<Typography variant="h6" className={styles.chartTitle}>
								Exam Simulation Trends
							</Typography>
							<ResponsiveContainer width="100%" height={300}>
								<LineChart data={safeData.dailyTrends}>
									<CartesianGrid strokeDasharray="3 3" />
									<XAxis dataKey="date" />
									<YAxis />
									<Tooltip />
									<Legend content={() => (
										<Box display="flex" flexWrap="wrap" gap={1}>
											{SERIES_CONFIG.filter(item => ['examGenerated', 'examGraded', 'studyPlanGenerated', 'tutorQuestions', 'benchmarkingUsed', 'insightGenerated'].includes(item.key)).map((item) => {
												const key = item.key;
												const active = !!visibleSeries[key];
												return (
													<Chip
														key={key}
														label={item.name}
														onClick={() => toggleSeries(key)}
														variant={active ? 'filled' : 'outlined'}
														sx={{ cursor: 'pointer', bgcolor: active ? item.color : 'transparent', color: active ? '#fff' : 'inherit', borderColor: item.color }}
													/>
												);
											})}
										</Box>
									)} />
									<Line type="monotone" dataKey="examGenerated" stroke="#8884d8" strokeWidth={2} name="Exams Generated" hide={!visibleSeries.examGenerated} />
									<Line type="monotone" dataKey="examGraded" stroke="#82ca9d" strokeWidth={2} name="Exams Graded" hide={!visibleSeries.examGraded} />
									<Line type="monotone" dataKey="studyPlanGenerated" stroke="#ffc658" strokeWidth={2} name="Study Plans" hide={!visibleSeries.studyPlanGenerated} />
									<Line type="monotone" dataKey="tutorQuestions" stroke="#ff7300" strokeWidth={2} name="Tutor Qs" hide={!visibleSeries.tutorQuestions} />
									<Line type="monotone" dataKey="benchmarkingUsed" stroke="#0088fe" strokeWidth={2} name="Benchmarking" hide={!visibleSeries.benchmarkingUsed} />
									<Line type="monotone" dataKey="insightGenerated" stroke="#00c49f" strokeWidth={2} name="Insights" hide={!visibleSeries.insightGenerated} />
								</LineChart>
							</ResponsiveContainer>
						</CardContent>
					</Card>
				</Grid>

				{/* User Type Distribution */}
				<Grid item xs={12} lg={4}>
					<Card className={styles.chartCard}>
						<CardContent>
							<Typography variant="h6" className={styles.chartTitle}>
								User Type Distribution
							</Typography>
							<ResponsiveContainer width="100%" height={300}>
								<PieChart>
									<Pie
										data={safeData.userTypeDistribution}
										cx="50%"
										cy="50%"
										outerRadius={80}
										dataKey="count"
										label={({ type, percentage }) => `${type}: ${Number(percentage || 0).toFixed(1)}%`}
									>
										{safeData.userTypeDistribution.map((entry, index) => (
											<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
										))}
									</Pie>
									<Tooltip />
								</PieChart>
							</ResponsiveContainer>
						</CardContent>
					</Card>
				</Grid>

				{/* Device Breakdown */}
				<Grid item xs={12} md={6}>
					<Card className={styles.chartCard}>
						<CardContent>
							<Typography variant="h6" className={styles.chartTitle}>
								Device Breakdown
							</Typography>
							<ResponsiveContainer width="100%" height={250}>
								<BarChart data={safeData.deviceBreakdown}>
									<CartesianGrid strokeDasharray="3 3" />
									<XAxis dataKey="device" />
									<YAxis />
									<Tooltip formatter={(value) => [`${value} users`, 'Count']} />
									<Bar dataKey="count" fill={COLORS[3]} />
								</BarChart>
							</ResponsiveContainer>
						</CardContent>
					</Card>
				</Grid>

				{/* Hourly Activity */}
				<Grid item xs={12} md={6}>
					<Card className={styles.chartCard}>
						<CardContent>
							<Typography variant="h6" className={styles.chartTitle}>
								Hourly Activity Pattern
							</Typography>
							<ResponsiveContainer width="100%" height={250}>
								<AreaChart data={safeData.hourlyActivity}>
									<CartesianGrid strokeDasharray="3 3" />
									<XAxis dataKey="hour" />
									<YAxis />
									<Tooltip />
									<Area
										type="monotone"
										dataKey="activity"
										stroke={COLORS[4]}
										fill={COLORS[4]}
										fillOpacity={0.6}
									/>
								</AreaChart>
							</ResponsiveContainer>
						</CardContent>
					</Card>
				</Grid>

				{/* Conversion Funnel */}
				<Grid item xs={12}>
					<Card className={styles.chartCard}>
						<CardContent>
							<Typography variant="h6" className={styles.chartTitle}>
								User Journey Overview
							</Typography>
							<ResponsiveContainer width="100%" height={300}>
								<BarChart data={safeData.conversionFunnel} layout="horizontal">
									<CartesianGrid strokeDasharray="3 3" />
									<XAxis type="number" />
									<YAxis dataKey="stage" type="category" width={120} />
									<Tooltip
										formatter={(value, name) => [
											name === 'count' ? `${formatNumber(value)} users` : `${value}%`,
											name === 'count' ? 'Count' : 'Conversion Rate',
										]}
									/>
									<Legend />
									<Bar dataKey="count" fill={COLORS[5]} name="Users" />
								</BarChart>
							</ResponsiveContainer>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
		</div>
	);
};

export default AnalyticsOverview;